<template>
  <div class="paypal-card-checkout">
    <!-- 卡片字段容器 -->
    <div class="card-fields-container">
      <div class="card-field" ref="cardNumber"></div>
      <div class="flex-container">
        <div class="card-field" ref="cardExpiry"></div>
        <div class="card-field" ref="cardCvv"></div>
      </div>
      <Input v-if="cardFieldsReady" type="text" v-model="cardholderName" placeholder="Name on the card" @on-blur="validateName"></Input>
    </div>
    <div v-if="errorMessage" class="error-message" v-html="errorMessage"></div>
    <div v-if="successMessage" class="success-message" v-html="successMessage"></div>
  </div>
</template>

<script>
import { paypalAddOrder, paypalCheckoutOrder, getPaypalToken } from '@/service/vbef'
export default {
  name: 'PayPalCardCheckout',
  props: {
    enable3DS: {
      type: Boolean,
      default: true,
    },
    scaMethod: {
      type: String,
      default: 'SCA_WHEN_REQUIRED',
    },
  },
  data() {
    return {
      sdkInstance: null, // 保存 SDK 实例
      cardSession: null, // 保存卡片会话实例
      sessionTimer: null, // 会话过期定时器
      isCardEligible: false, // 是否支持卡片支付
      errorMessage: '', // 错误提示
      successMessage: '', // 成功提示
      cardholderName: '', // 持卡人姓名
      liabilityShift: '',
      cardFieldsReady: false,
    }
  },
  mounted() {
    // 挂载后加载 PayPal SDK
    this.loadPayPalSdk()
  },
  beforeDestroy() {
    // 组件销毁前清理资源
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer)
      this.sessionTimer = null
    }
    if (this.cardSession) {
      this.cardSession = null
    }
    if (this.sdkInstance) {
      this.sdkInstance = null
    }
    // 移除已加载的 SDK 脚本，避免重复加载
    const sdkScript = document.querySelector('script[src*="paypal.com/web-sdk/v6/core"]')
    if (sdkScript) {
      sdkScript.remove()
    }
  },
  methods: {
    
    /**
     * 加载 PayPal SDK 脚本
     */
    loadPayPalSdk() {
      // 检查是否已加载 SDK，避免重复加载
      if (window.paypal) {
        this.initPayPalSdk()
        return
      }
      // 创建 SDK 脚本标签
      const script = document.createElement('script')
      script.src = this.$store.state.paypalScript
      script.async = true
      script.onload = () => this.initPayPalSdk()
      script.onerror = () => {
        this.errorMessage =
          'PayPal payment component failed to load, please refresh the page and try again'
      }
      document.body.appendChild(script)
    },
    /**
     * 初始化 PayPal SDK，并检查卡片支付是否可用
     */
    async initPayPalSdk() {
      try {
        // 获取浏览器安全客户端令牌
        const clientToken = await this.getBrowserSafeClientToken()
        this.sdkInstance = await window.paypal.createInstance({
          clientToken,
          components: ['card-fields'],
          pageType: 'checkout',
        })
        const paymentMethods = await this.sdkInstance.findEligibleMethods()
        this.isCardEligible = paymentMethods.isEligible('advanced_cards')
        if (this.isCardEligible) {
          this.renderCardFields()
        } else {
          this.errorMessage =
            'Card payment is not supported currently, please choose another payment method'
        }
      } catch (err) {
        console.error('SDK 初始化失败:', err)
        this.errorMessage = 'Payment component initialization failed, please try again later'
      }
    },
    /**
     * 渲染卡片字段
     */
    renderCardFields() {
      this.cardFieldsReady = false
      this.cardholderName = ''
      // 清空旧的 DOM，防止重新创建时重复挂载
      if (this.$refs.cardNumber) this.$refs.cardNumber.innerHTML = ''
      if (this.$refs.cardExpiry) this.$refs.cardExpiry.innerHTML = ''
      if (this.$refs.cardCvv) this.$refs.cardCvv.innerHTML = ''

      this.cardSession = this.sdkInstance.createCardFieldsOneTimePaymentSession()
      const styleParams = {
        input: {
          fontSize: '16px',
          fontWeight: '500',
          fontFamily: 'Roboto, Roboto',
          lineHeight: '1.5',
          padding: '8px 16px',
          border: '1px solid #D1D5DB',
          borderRadius: '4px',
          color: '#374151',
          background: '#FFFFFF',
          transition: 'all 0.2s ease-in-out',
        },
        'input::placeholder': {
          color: '#9CA3AF',
        },
        '.invalid': {
          border: '1px solid #F44040',
        },
        '.invalid.focus': {
          border: '1px solid #F44040',
        },
        'input.focus': {
          outline: 'none',
          border: '1px solid #4B5563',
        },
      }
      const numberField = this.cardSession.createCardFieldsComponent({
        type: 'number',
        placeholder: 'Card Number',
        style: styleParams,
      })
      const expiryField = this.cardSession.createCardFieldsComponent({
        type: 'expiry',
        placeholder: 'MM / YY',
        style: styleParams,
      })
      const cvvField = this.cardSession.createCardFieldsComponent({
        type: 'cvv',
        placeholder: 'CVV',
        style: styleParams,
      })
      this.$refs.cardNumber.appendChild(numberField)
      this.$refs.cardExpiry.appendChild(expiryField)
      this.$refs.cardCvv.appendChild(cvvField)

      // 启动或重置会话过期定时器
      this.resetSessionTimer()
      this.cardFieldsReady = true
    },
    /**
     * 重置会话过期定时器
     */
    resetSessionTimer() {
      if (this.sessionTimer) {
        clearTimeout(this.sessionTimer)
      }
      // 10分钟后重新创建 Session，提前1分钟避免临界过期
      this.sessionTimer = setTimeout(() => {
        this.errorMessage = 'The payment session has expired due to inactivity. Please re-enter your card details.'
        this.recreateSession()
      }, 10 * 60 * 1000)
    },
    /**
     * 重新创建 Session
     */
    recreateSession() {
      this.cardSession = null
      this.cardFieldsReady = false
      this.cardholderName = ''
      // 重新初始化 SDK 获取新 token 并渲染
      this.initPayPalSdk()
    },
    /**
     * 处理支付事件，创建订单并提交支付信息
     */
    async handlePay(orderParams, formParams) {
      if (!this.cardSession) {
        this.errorMessage = 'Payment component is not initialized, please refresh the page'
        this.$emit('updateLoading', false)
        return
      }

      if (!this.validateName()) {
        this.$emit('updateLoading', false)
        return
      }

      try {
        this.errorMessage = ''
        this.successMessage = ''
        this.liabilityShift = ''

        // 创建订单
        const { activityOrderNumber, paypalOrderId } = await this.createOrder(orderParams)
        // 提交卡片信息和订单
        const billingAddress = {}
        if (formParams && formParams.email) billingAddress.email = formParams.email.trim()
        if (formParams && formParams.postalCode) billingAddress.postalCode = formParams.postalCode.trim()
        if (formParams && formParams.countryCode) billingAddress.countryCode = formParams.countryCode.trim()

        const submitOptions = { name: this.cardholderName.trim() }
        if (Object.keys(billingAddress).length) submitOptions.billingAddress = billingAddress

        const { data = {}, state } = await this.cardSession.submit(paypalOrderId, submitOptions)
        this.liabilityShift = data.liabilityShift || ''

        // 处理支付状态
        switch (state) {
          case 'succeeded': {
            const captureResult = await this.captureOrder(activityOrderNumber)
            if (captureResult.code == 0 && captureResult.data.status == 2) {
              this.successMessage = 'Payment successful!'
              this.$router.push({
                name: 'ticketDetail',
                params: { id: captureResult.data.activityTicketUserRelationId },
              })
            } else {
              this.errorMessage = `Order confirmation error. Please check your <a href="/myTickets">purchased tickets</a> page or contact us`
            }
            break
          }
          case 'canceled': {
            this.errorMessage = 'You canceled the payment verification, please try again'
            break
          }
          case 'failed': {
            this.errorMessage = data.message || 'Payment failed. Check your details and try again.'
            console.warn('支付失败:', state, data)
            break
          }
          default: {
            this.errorMessage = 'Payment status is abnormal, please contact customer service'
            console.warn('未处理的状态:', state, data)
          }
        }
      } catch (err) {
        console.error('支付失败:', err)
        this.errorMessage = 'An error occurred during payment, please try again later'
      } finally {
        this.recreateSession()
        this.$emit('updateLoading', false)
      }
    },
    /**
     * 获取浏览器安全令牌
     */
    async getBrowserSafeClientToken() {
      try {
        const res = await getPaypalToken()
        return res.code === 0 ? res.data.token : ''
      } catch (err) {
        throw new Error('获取支付令牌失败: ' + (err.message || '网络错误'))
      }
    },
    /**
     * 创建订单
     */
    async createOrder(orderParams) {
      try {
        // const createOrderParams = this.enable3DS
        //   ? { ...orderParams, enable3DS: true, scaMethod: this.scaMethod }
        //   : { ...orderParams }
        const res = await paypalAddOrder(orderParams)
        if (res.code === 0) {
          return res.data
        } else {
          this.errorMessage = 'An error occurred during payment, please try again later'
        }
      } catch (err) {
        throw new Error('创建订单失败: ' + (err.message || '网络错误'))
      }
    },
    /**
     * 捕获订单
     */
    async captureOrder(orderId) {
      try {
        const res = await paypalCheckoutOrder({
          activityOrderNumber: orderId,
        })
        return res
      } catch (err) {
        throw new Error('确认支付失败: ' + (err.message || '网络错误'))
      }
    },
    /**
     * 验证持卡人姓名
     */
    validateName() {
      // 去除首尾空格
      const trimmedName = this.cardholderName.trim()

      // 验证是否为空（必填校验）
      if (!trimmedName) {
        this.errorMessage = 'Please enter the cardholder name'
        return false
      }

      // 验证通过，清空错误提示
      this.errorMessage = ''
      return true
    },
  },
}
</script>
<style lang="stylus" scoped>
.error-message
  font-weight 500
  color #ED4014
  padding-top 6px
  font-size 14px
  line-height 1.4
  a
    text-decoration underline
    color #1572E7
.success-message
  font-weight 500
  color #67C23A
  padding-top 6px
  font-size 14px
  line-height 1.4
.card-fields-container
  display flex
  flex-direction column
  gap 20px
  .card-field
    height 50px
  >>> .ivu-input
    font-weight 500
    font-family Roboto, Roboto
    height 50px
    border 1px solid #D1D5DB
    border-radius 4px
    font-size 16px
    padding 8px 16px
    color #374151
    background #FFFFFF
    transition all 0.2s ease-in-out
    box-shadow none
    &:focus
      outline none
      border-color #4B5563
    &::placeholder
      color #9CA3AF
.flex-container
  display flex
  gap 16px
  .card-field
    flex 1
</style>
